import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ChairIcon from "@mui/icons-material/Chair";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  LIBRARY_ADMISSION_RULES,
  LIBRARY_CONSENT_VERSION,
} from "../constants/admissionConsent";

const API = process.env.REACT_APP_API_URL;

const LibraryBookingForm = ({ seatId, open, onClose, onSuccess }) => {
  const [seat, setSeat] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [purposeOfJoining, setPurposeOfJoining] = useState("");
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const [hasProfilePhoto, setHasProfilePhoto] = useState(true);
  const [student, setStudent] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [errors, setErrors] = useState({});
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!seatId) return;
    const fetchData = async () => {
      const loadingToast = toast.loading("Loading...");
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const [seatRes, profileRes] = await Promise.all([
          axios.get(`${API}library/seats/${seatId}/`, config),
          axios.get(`${API}users/me/`, config),
        ]);

        setSeat(seatRes.data);
        setStudent(profileRes.data);
        setHasProfilePhoto(!!profileRes.data.profile_photo);
        setBillingSummary(null);
        toast.dismiss(loadingToast);
      } catch (err) {
        toast.dismiss(loadingToast);
        toast.error("❌ Failed to fetch seat or profile.");
        onClose();
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatId]);

  // Reset form when opened
  useEffect(() => {
    if (open) {
      setStartDate("");
      setPurposeOfJoining("");
      setAadhaarFront(null);
      setAadhaarBack(null);
      setStudentPhoto(null);
      setConsentAccepted(false);
      setConfirmOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !seatId || !startDate) {
      setBillingSummary(null);
      return;
    }

    const fetchBillingSummary = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API}library/billing-summary/?seat_id=${seatId}&start_date=${startDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBillingSummary(response.data);
      } catch (err) {
        setBillingSummary(null);
        toast.error("Failed to calculate billing for the selected date.");
      }
    };

    fetchBillingSummary();
  }, [open, seatId, startDate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};

    // Validate required fields
    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!purposeOfJoining.trim()) {
      newErrors.purposeOfJoining = "Purpose of joining is required";
    }

    // Only require Aadhaar photos if student doesn't have them
    if (!student?.aadhaar_front_photo && !aadhaarFront) {
      newErrors.aadhaarFront = "Aadhaar front photo is required";
    }

    if (!student?.aadhaar_back_photo && !aadhaarBack) {
      newErrors.aadhaarBack = "Aadhaar back photo is required";
    }

    if (!hasProfilePhoto && !studentPhoto) {
      newErrors.studentPhoto = "Profile photo is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setConfirmOpen(true);
  };

  const submitFinalBooking = async () => {
    if (!consentAccepted || isSubmitting) return;

    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Loading...");
    setIsSubmitting(true);
    try {
      if (!hasProfilePhoto && studentPhoto) {
        const photoForm = new FormData();
        photoForm.append("profile_photo", studentPhoto);
        await axios.patch(`${API}users/update-profile-photo/`, photoForm, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      const formData = new FormData();
      formData.append("seat_id", seatId);
      formData.append("start_date", startDate);
      formData.append("purpose_of_joining", purposeOfJoining);
      formData.append("consent_accepted", "true");

      // Only append Aadhaar photos if they are provided
      if (aadhaarFront) {
        formData.append("aadhaar_front_photo", aadhaarFront);
      }
      if (aadhaarBack) {
        formData.append("aadhaar_back_photo", aadhaarBack);
      }

      await axios.post(`${API}library/bookings/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.dismiss(loadingToast);
      toast.success("✅ Booking request sent!");
      setConfirmOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      const responseData = err.response?.data;
      const firstError =
        responseData && typeof responseData === "object"
          ? Object.values(responseData).flat().find(Boolean)
          : null;
      const msg =
        responseData?.error ||
        responseData?.detail ||
        firstError ||
        "❌ Booking failed. Check your input.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const bookingDetails = [
    { label: "Monthly Fee", value: `₹${billingSummary?.monthly_fee || 0}` },
    { label: "Deposit", value: `₹${billingSummary?.deposit_amount || 0}` },
    { label: "Total", value: `₹${billingSummary?.total || 0}` },
  ];

  const FileUploadButton = ({
    label,
    onChange,
    required = false,
    accept = "image/png,image/jpg,image/jpeg",
    value = null,
    error = null,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={onChange}
          className="hidden"
          id={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          name={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
        />
        <label
          htmlFor={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          className={`flex flex-col items-center justify-center gap-1 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
            error
              ? "border-red-500 bg-red-50 text-red-700"
              : value
              ? "border-green-500 bg-green-50 text-green-700"
              : "border-gray-300 text-gray-600 hover:border-blue-400"
          }`}
        >
          {value ? (
            <>
              <CheckCircleIcon className="text-green-600" />
              <span className="font-medium">{value.name}</span>
            </>
          ) : (
            <>
              <CloudUploadIcon />
              <span className="font-medium">Click to upload</span>
              <span className="text-xs text-gray-500">
                Max file size: 2MB | Accepted: PNG, JPG, JPEG
              </span>
            </>
          )}
        </label>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6">
        <DialogTitle className="flex items-center gap-3 text-white font-bold text-xl">
          <ChairIcon className="text-2xl" />
          Book Library Seat
        </DialogTitle>
      </div>

      <DialogContent className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
        {seat && (
          <form
            id="library-booking-form"
            onSubmit={handleSubmit}
            className="space-y-6"
            noValidate
          >
            {/* Seat Information Card */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
              <Typography className="text-lg font-semibold text-gray-800 mb-2">
                Seat Information
              </Typography>
              <Typography className="text-gray-600">
                Selected Seat:{" "}
                <span className="font-semibold text-blue-600">
                  {seat.seat_number}
                </span>
              </Typography>
            </div>

            {/* Booking Details Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <Typography className="text-lg font-semibold text-gray-800 mb-4">
                Booking Details
              </Typography>

              <div className="space-y-4 mt-4">
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (errors.startDate) {
                      setErrors((prev) => ({ ...prev, startDate: null }));
                    }
                  }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                  className="mb-4"
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />

                <TextField
                  fullWidth
                  type="text"
                  label="Purpose of joining"
                  value={purposeOfJoining}
                  onChange={(e) => {
                    setPurposeOfJoining(e.target.value);
                    if (errors.purposeOfJoining) {
                      setErrors((prev) => ({
                        ...prev,
                        purposeOfJoining: null,
                      }));
                    }
                  }}
                  required
                  multiline
                  rows={3}
                  className="mb-4"
                  error={!!errors.purposeOfJoining}
                  helperText={errors.purposeOfJoining}
                />
              </div>
            </div>

            {/* Document Upload Card */}
            {(!student?.aadhaar_front_photo ||
              !student?.aadhaar_back_photo ||
              !hasProfilePhoto) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <Typography className="text-lg font-semibold text-gray-800 mb-4">
                  Required Documents
                </Typography>

                <div className="space-y-4 mt-4">
                  {!student?.aadhaar_front_photo && (
                    <FileUploadButton
                      label="Aadhaar Front Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("File must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setAadhaarFront(file);
                          if (errors.aadhaarFront) {
                            setErrors((prev) => ({
                              ...prev,
                              aadhaarFront: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={aadhaarFront}
                      error={errors.aadhaarFront}
                    />
                  )}

                  {!student?.aadhaar_back_photo && (
                    <FileUploadButton
                      label="Aadhaar Back Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("File must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setAadhaarBack(file);
                          if (errors.aadhaarBack) {
                            setErrors((prev) => ({
                              ...prev,
                              aadhaarBack: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={aadhaarBack}
                      error={errors.aadhaarBack}
                    />
                  )}

                  {!hasProfilePhoto && (
                    <FileUploadButton
                      label="Profile Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("Profile photo must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setStudentPhoto(file);
                          if (errors.studentPhoto) {
                            setErrors((prev) => ({
                              ...prev,
                              studentPhoto: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={studentPhoto}
                      error={errors.studentPhoto}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Billing Summary Card */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <Typography className="text-lg font-semibold text-gray-800 mb-4">
                Billing Summary
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {bookingDetails.map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-sm text-gray-600 mb-1">
                      {item.label}
                    </div>
                    <div className="text-xl font-bold text-gray-800">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        )}
      </DialogContent>

      <DialogActions className="justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <Button onClick={onClose} variant="outlined" className="px-6 py-2">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          form="library-booking-form"
          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          Submit Booking
        </Button>
      </DialogActions>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <DialogTitle className="text-white font-bold text-xl">
            Confirm Booking
          </DialogTitle>
        </div>
        <DialogContent className="p-6 space-y-3">
          <div className="bg-gray-50 p-4 rounded-lg">
            <Typography className="font-semibold text-gray-800 mb-3">
              Bussiness Track Hostel & Library
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">
                  {student.first_name} {student.last_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{student.phone_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{student.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Address:</span>
                <span className="font-medium">{student.address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Seat:</span>
                <span className="font-medium">{seat?.seat_number}</span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <Typography className="font-semibold text-gray-800 mb-2">
              Payment Summary
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Fee:</span>
                <span className="font-medium">
                  ₹{billingSummary?.monthly_fee || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit:</span>
                <span className="font-medium">
                  ₹{billingSummary?.deposit_amount || 0}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-800 font-semibold">Total:</span>
                <span className="text-lg font-bold text-green-600">
                  ₹{billingSummary?.total || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <Typography className="font-semibold text-gray-800 mb-2">
              Library Admission Rules &amp; Consent
            </Typography>
            <Typography variant="body2" className="text-gray-600 mb-3">
              Please read every rule before accepting. Consent is compulsory for every booking.
            </Typography>
            <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white p-3">
              <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
                {LIBRARY_ADMISSION_RULES.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ol>
            </div>
            <FormControlLabel
              className="mt-3 items-start"
              control={
                <Checkbox
                  checked={consentAccepted}
                  onChange={(event) => setConsentAccepted(event.target.checked)}
                  color="primary"
                />
              }
              label={`I have read, understood, and agree to all Library admission rules (Version ${LIBRARY_CONSENT_VERSION}).`}
            />
          </div>
        </DialogContent>
        <DialogActions className="p-6">
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            className="px-6 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={submitFinalBooking}
            disabled={!consentAccepted || isSubmitting}
            variant="contained"
            color="primary"
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
          >
            {isSubmitting ? "Booking..." : "Confirm & Book"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LibraryBookingForm;
